<template>
  <div class="user-dashboard">
    <div v-if="!embedded" class="dashboard-page-actions">
      <span class="dashboard-page-actions__meta">{{ $t('common.lastUpdated') }}: {{ formatRefreshTime(lastUpdatedAt) }}</span>
      <CButton color="primary" class="font-weight-bold" size="sm" :disabled="dashboardLoading" @click="loadDashboard">
        {{ $t('common.refresh') }}
      </CButton>
    </div>

    <CCard v-if="!embedded" class="dashboard-hero border-0 shadow-sm mb-4">
      <CCardBody class="p-4 p-lg-5">
        <CRow class="align-items-center">
          <CCol lg="8">
            <div class="hero-kicker">{{ $t('pages.dashboard.user.heroKicker') }}</div>
            <h2 class="hero-title">{{ $t('pages.dashboard.user.heroTitle') }}</h2>
            <p class="hero-copy">
              {{ $t('pages.dashboard.user.heroCopy') }}
            </p>
            <div class="hero-actions">
              <CButton color="primary" class="font-weight-bold mr-2 mb-2" to="/dashboard/user-agreements">
                {{ $t('pages.dashboard.user.myAgreements') }}
              </CButton>
              <CButton color="secondary" variant="outline" class="mr-2 mb-2" to="/dashboard/user-review-queue">
                {{ $t('pages.dashboard.user.myReviewQueue') }}
              </CButton>
              <CButton color="secondary" variant="outline" class="mb-2" to="/dashboard/user-translation">
                {{ $t('pages.dashboard.user.translationRequests') }}
              </CButton>
            </div>
          </CCol>
          <CCol lg="4">
            <CCard class="hero-side-card border-0 shadow-sm mt-4 mt-lg-0">
              <CCardBody>
                <div class="hero-side-label">{{ $t('pages.dashboard.user.inReview') }}</div>
                <div class="hero-side-value">{{ reviewCount }}</div>
                <div class="hero-side-copy">{{ $t('pages.dashboard.user.inReviewCopy') }}</div>
              </CCardBody>
            </CCard>
          </CCol>
        </CRow>
      </CCardBody>
    </CCard>

    <CRow class="mb-2">
      <CCol md="6" xl="4" v-for="item in summaryStats" :key="item.label" class="mb-4">
        <CCard class="stat-card border-0 shadow-sm h-100" @click.native="goTo(item.to)">
          <CCardBody>
            <div class="stat-header">
              <div class="stat-label">{{ item.label }}</div>
              <div class="stat-icon" :class="item.iconClass">{{ item.icon }}</div>
            </div>
            <div class="stat-value">{{ item.value }}</div>
            <div class="stat-delta">{{ item.delta }}</div>
          </CCardBody>
        </CCard>
      </CCol>
    </CRow>

    <CRow>
      <CCol lg="8" class="mb-4">
        <CCard class="panel-card border-0 shadow-sm h-100">
          <CCardHeader class="panel-header bg-white border-0">
            <div>
              <div class="panel-title">{{ $t('pages.dashboard.user.agreementStatusTitle') || 'Agreement Status' }}</div>
              <div class="panel-subtitle">{{ $t('pages.dashboard.user.agreementStatusSubtitle') || 'Overview of your agreements by approval status' }}</div>
            </div>
          </CCardHeader>
          <CCardBody class="chart-card-body pt-0">
            <CChartBar
              :datasets="activityChart.datasets"
              :labels="activityChart.labels"
              :options="chartOptions"
            />
          </CCardBody>
        </CCard>
      </CCol>

      <CCol lg="4" class="mb-4">
        <CCard class="panel-card border-0 shadow-sm h-100">
          <CCardHeader class="panel-header bg-white border-0">
            <div>
              <div class="panel-title">{{ $t('pages.dashboard.user.quickActionsTitle') }}</div>
              <div class="panel-subtitle">{{ $t('pages.dashboard.user.quickActionsSubtitle') }}</div>
            </div>
          </CCardHeader>
          <CCardBody class="pt-0">
            <button v-for="item in quickActions" :key="item.label" class="quick-action" @click="goTo(item.to)">
              <span class="quick-action__icon">{{ item.icon }}</span>
              <span class="quick-action__content">
                <span class="quick-action__label">{{ item.label }}</span>
                <span class="quick-action__copy">{{ item.copy }}</span>
              </span>
              <span class="quick-action__badge">{{ item.badge }}</span>
            </button>
          </CCardBody>
        </CCard>
      </CCol>
    </CRow>

    <CRow>
      <CCol lg="7" class="mb-4">
        <CCard class="panel-card border-0 shadow-sm h-100">
          <CCardHeader class="panel-header bg-white border-0">
            <div>
              <div class="panel-title">{{ $t('pages.dashboard.user.recentAgreementsTitle') }}</div>
              <div class="panel-subtitle">{{ $t('pages.dashboard.user.recentAgreementsSubtitle') }}</div>
            </div>
          </CCardHeader>
          <CCardBody class="pt-0">
            <div v-if="recentAgreements.length">
              <div v-for="item in recentAgreements" :key="item.id" class="queue-item">
                <div class="queue-item__content">
                  <div class="queue-item__title">{{ item.title }}</div>
                  <div class="queue-item__meta">{{ item.partner }} · {{ item.status }}</div>
                </div>
                <CButton size="sm" color="secondary" variant="outline" @click="openAgreement(item)">
                  {{ $t('pages.dashboard.user.open') }}
                </CButton>
              </div>
            </div>
            <div v-else class="empty-copy">{{ $t('pages.dashboard.user.noAgreementsYet') }}</div>
          </CCardBody>
        </CCard>
      </CCol>

      <CCol lg="5" class="mb-4">
        <CCard class="panel-card border-0 shadow-sm h-100">
          <CCardHeader class="panel-header bg-white border-0">
            <div>
              <div class="panel-title">{{ $t('pages.dashboard.user.recentTranslationsTitle') }}</div>
              <div class="panel-subtitle">{{ $t('pages.dashboard.user.recentTranslationsSubtitle') }}</div>
            </div>
          </CCardHeader>
          <CCardBody class="pt-0">
            <div v-if="recentTranslations.length">
              <div v-for="item in recentTranslations" :key="item.id" class="queue-item">
                <div class="queue-item__content">
                  <div class="queue-item__title">{{ item.agreementTitle }}</div>
                  <div class="queue-item__meta">{{ item.targetLanguageLabel }} · {{ item.status }}</div>
                </div>
              </div>
            </div>
            <div v-else class="empty-copy">{{ $t('pages.dashboard.user.noTranslationRequestsYet') }}</div>
          </CCardBody>
        </CCard>
      </CCol>
    </CRow>
  </div>
</template>

<script>
import { CChartBar } from '@coreui/vue-chartjs'
import Service from '@/service/api'
import { readPageCache, writePageCache } from '@/utils/page-cache'

const USER_DASHBOARD_CACHE_KEY = 'user-dashboard-page'
const USER_DASHBOARD_CACHE_MAX_AGE = 5 * 60 * 1000

export default {
  name: 'DashboardUser',
  props: {
    embedded: {
      type: Boolean,
      default: false
    }
  },
  components: {
    CChartBar
  },
  data() {
    return {
      agreements: [],
      lastUpdatedAt: '',
      dashboardLoading: false,
      reports: {
        stats: [],
        translationStats: [],
        monthly: [],
        recentAgreements: [],
        recentTranslations: []
      },
      chartOptions: {
        maintainAspectRatio: true,
        aspectRatio: 2,
        responsive: true,
        legend: {
          position: 'bottom'
        },
        scales: {
          yAxes: [{
            ticks: {
              beginAtZero: true,
              precision: 0
            }
          }]
        }
      }
    }
  },
  computed: {
    draftCount() {
      return this.agreements.filter(item => item.derivedStage === 'draft').length
    },
    reviewCount() {
      return this.agreements.filter(item => item.derivedStage !== 'draft' && item.derivedStage !== 'approved').length
    },
    approvedCount() {
      return this.agreements.filter(item => item.derivedStage === 'approved').length
    },
    summaryStats() {
      return [
        {
          label: this.$t('pages.dashboard.user.drafts'),
          value: this.draftCount,
          delta: this.$t('pages.dashboard.user.draftsDelta'),
          to: '/dashboard/user-agreements',
          icon: 'D',
          iconClass: 'stat-icon--neutral'
        },
        {
          label: this.$t('pages.dashboard.user.reviewCount'),
          value: this.reviewCount,
          delta: this.$t('pages.dashboard.user.reviewCountDelta'),
          to: '/dashboard/user-review-queue',
          icon: 'R',
          iconClass: 'stat-icon--warning'
        },
        {
          label: this.$t('pages.dashboard.user.approved'),
          value: this.approvedCount,
          delta: this.$t('pages.dashboard.user.approvedDelta'),
          to: '/dashboard/user-reports',
          icon: 'A',
          iconClass: 'stat-icon--success'
        }
      ]
    },
    activityChart() {
      // Count agreements by status
      const approved = this.agreements.filter(item => item.derivedStage === 'approved').length
      const rejected = this.agreements.filter(item => item.derivedStage === 'rejected').length
      const active = this.agreements.filter(item => item.derivedStage !== 'approved' && item.derivedStage !== 'rejected' && item.derivedStage !== 'draft').length
      
      return {
        labels: [
          this.$t('pages.dashboard.user.approved'),
          this.$t('pages.dashboard.user.rejected') || 'Rejected',
          this.$t('pages.dashboard.user.active') || 'On Progress'
        ],
        datasets: [
          {
            label: this.$t('pages.dashboard.user.approved'),
            data: [approved],
            backgroundColor: 'rgba(46, 184, 92, 0.75)',
            borderRadius: 10
          },
          {
            label: this.$t('pages.dashboard.user.rejected') || 'Rejected',
            data: [rejected],
            backgroundColor: 'rgba(220, 53, 69, 0.75)',
            borderRadius: 10
          },
          {
            label: this.$t('pages.dashboard.user.active') || 'On Progress',
            data: [active],
            backgroundColor: 'rgba(34, 94, 168, 0.75)',
            borderRadius: 10
          }
        ]
      }
    },
    quickActions() {
      return [
        {
          label: this.$t('pages.dashboard.user.createAgreement'),
          copy: this.$t('pages.dashboard.user.createAgreementCopy'),
          badge: this.draftCount,
          icon: 'N',
          to: '/dashboard/user-agreements/new'
        },
        {
          label: this.$t('pages.dashboard.user.openReviewQueue'),
          copy: this.$t('pages.dashboard.user.openReviewQueueCopy'),
          badge: this.reviewCount,
          icon: 'Q',
          to: '/dashboard/user-review-queue'
        },
        {
          label: this.$t('pages.dashboard.user.requestTranslation'),
          copy: this.$t('pages.dashboard.user.requestTranslationCopy'),
          badge: Array.isArray(this.reports.translationStats) && this.reports.translationStats[0] ? this.reports.translationStats[0].value : 0,
          icon: 'T',
          to: '/dashboard/user-translation'
        }
      ]
    },
    recentAgreements() {
      return Array.isArray(this.reports.recentAgreements) ? this.reports.recentAgreements : []
    },
    recentTranslations() {
      return Array.isArray(this.reports.recentTranslations) ? this.reports.recentTranslations : []
    }
  },
  mounted() {
    this.loadCachedDashboard()
    this.loadDashboard()
  },
  methods: {
    formatRefreshTime(value) {
      if (!value) return this.$t('pages.dashboard.user.justNow')
      return new Date(value).toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit'
      })
    },
    loadCachedDashboard() {
      const cached = readPageCache(USER_DASHBOARD_CACHE_KEY, USER_DASHBOARD_CACHE_MAX_AGE)
      if (!cached || typeof cached !== 'object') return
      this.agreements = Array.isArray(cached.agreements) ? cached.agreements : []
      this.reports = cached.reports && typeof cached.reports === 'object' ? cached.reports : this.reports
      this.lastUpdatedAt = cached.lastUpdatedAt || ''
    },
    async loadDashboard() {
      if (this.dashboardLoading) return
      this.dashboardLoading = true
      try {
        const [agreements, reports] = await Promise.all([
          Service.agreements('list'),
          Service.userWorkspace('reports')
        ])
        this.agreements = agreements && agreements.data && agreements.data.data ? agreements.data.data : []
        this.reports = reports && reports.data && reports.data.data ? reports.data.data : this.reports
        this.lastUpdatedAt = new Date().toISOString()
        writePageCache(USER_DASHBOARD_CACHE_KEY, {
          agreements: this.agreements,
          reports: this.reports,
          lastUpdatedAt: this.lastUpdatedAt
        })
      } catch (error) {
        if (!this.lastUpdatedAt) {
          this.agreements = []
        }
      } finally {
        this.dashboardLoading = false
      }
    },
    goTo(path) {
      if (!path) return
      this.$router.push(path)
    },
    openAgreement(item) {
      const match = this.agreements.find(entry => String(entry.id) === String(item.id))
      if (!match) {
        this.$router.push('/dashboard/user-agreements')
        return
      }
      if (match.derivedStage === 'submitted' || match.derivedStage === 'suggestion' || match.derivedStage === 'revision') {
        this.$router.push(`/dashboard/user-agreements/${match.id}/start-review`)
        return
      }
      if (match.derivedStage === 'approved' || match.derivedStage === 'final-review') {
        this.$router.push(`/dashboard/user-agreements/${match.id}/review-summary?mode=view`)
        return
      }
      this.$router.push(`/dashboard/user-agreements/${match.id}/${match.routeType}`)
    }
  }
}
</script>

<style scoped>
.dashboard-page-actions {
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  justify-content: flex-end;
  margin-bottom: 10px;
  padding: 0 2px;
}

.dashboard-page-actions__meta {
  background: #ffffff;
  border: 1px solid #dbe3ee;
  border-radius: 999px;
  color: #475569;
  font-size: 0.82rem;
  font-weight: 700;
  line-height: 1;
  padding: 9px 12px;
}

.dashboard-hero {
  border-radius: 1.5rem !important;
  border: 0 !important;
  overflow: hidden;
  box-shadow: 0 20px 40px rgba(44, 52, 71, 0.1) !important;
  background:
    radial-gradient(circle at top right, rgba(254, 194, 96, 0.28), transparent 28%),
    radial-gradient(circle at left center, rgba(255, 255, 255, 0.12), transparent 26%),
    linear-gradient(135deg, #5d0f12 0%, #8c1515 46%, #c77f28 100%) !important;
}

.hero-kicker {
  color: rgba(255, 248, 240, 0.8);
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  margin-bottom: 0.75rem;
  text-transform: uppercase;
}

.hero-title {
  color: #fff8f1;
  font-size: 2rem;
  font-weight: 700;
  line-height: 1.15;
  margin-bottom: 0.85rem;
  max-width: 42rem;
}

.hero-copy {
  color: rgba(255, 246, 237, 0.86);
  font-size: 1rem;
  margin-bottom: 1.25rem;
  max-width: 40rem;
}

.hero-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
}

.hero-actions .btn {
  font-weight: 600;
  padding: 0.6rem 1.5rem;
  border-radius: 0.5rem;
  transition: all 0.3s ease;
}

.hero-actions .btn-primary {
  background-color: #fff8f1;
  color: #8c1515;
  border-color: #fff8f1;
}

.hero-actions .btn-primary:hover {
  background-color: #fff0e0;
  border-color: #fff0e0;
  transform: translateY(-2px);
}

.hero-actions .btn-outline-secondary {
  color: #fff8f1;
  border-color: #fff8f1;
}

.hero-actions .btn-outline-secondary:hover {
  background-color: rgba(255, 246, 237, 0.15);
  border-color: #fff8f1;
  transform: translateY(-2px);
}

.hero-side-card {
  border-radius: 1rem !important;
  border: 0 !important;
  color: #233247;
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.96), rgba(247, 249, 252, 0.98)) !important;
}

.hero-side-label,
.stat-label {
  font-size: 0.8rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}

.hero-side-label {
  color: #8c1515;
}

.hero-side-value,
.stat-value {
  font-size: 2rem;
  font-weight: 700;
  line-height: 1.1;
  margin-top: 0.5rem;
}

.hero-side-copy,
.stat-delta,
.panel-subtitle,
.queue-item__meta,
.empty-copy {
  color: #7b8794;
}

.stat-card,
.panel-card {
  border-radius: 1rem;
  overflow: hidden;
}

.stat-card {
  cursor: pointer;
}

.stat-header {
  align-items: center;
  display: flex;
  justify-content: space-between;
  gap: 1rem;
}

.stat-label {
  color: #8c1515;
}

.stat-icon {
  align-items: center;
  border-radius: 1rem;
  display: inline-flex;
  font-size: 0.95rem;
  font-weight: 700;
  height: 2.8rem;
  justify-content: center;
  width: 2.8rem;
}

.stat-icon--neutral {
  background: linear-gradient(135deg, rgba(140, 21, 21, 0.12), rgba(254, 194, 96, 0.2));
  color: #8c1515;
}

.stat-icon--warning {
  background: linear-gradient(135deg, rgba(254, 194, 96, 0.18), rgba(140, 21, 21, 0.1));
  color: #9a5d00;
}

.stat-icon--success {
  background: linear-gradient(135deg, rgba(46, 184, 92, 0.12), rgba(46, 184, 92, 0.2));
  color: #2eb85c;
}

.panel-header {
  padding: 1.35rem 1.5rem 0.75rem;
}

.panel-title {
  color: #183b56;
  font-size: 1.35rem;
  font-weight: 700;
}

.chart-card-body {
  min-height: 320px;
}

.quick-action {
  align-items: center;
  background: transparent;
  border: 0;
  border-top: 1px solid #eef1f6;
  display: flex;
  gap: 0.9rem;
  padding: 1rem 0;
  text-align: left;
  width: 100%;
}

.quick-action__icon {
  align-items: center;
  background: #f8fafc;
  border-radius: 0.9rem;
  color: #8c1515;
  display: inline-flex;
  font-size: 0.85rem;
  font-weight: 700;
  height: 2.4rem;
  justify-content: center;
  min-width: 2.4rem;
}

.quick-action__content {
  display: flex;
  flex: 1;
  flex-direction: column;
}

.quick-action__label,
.queue-item__title {
  color: #183b56;
  font-weight: 700;
}

.quick-action__copy {
  color: #7b8794;
  font-size: 0.88rem;
  margin-top: 0.1rem;
}

.quick-action__badge {
  background: #fff4df;
  border-radius: 999px;
  color: #8c1515;
  font-size: 0.78rem;
  font-weight: 700;
  padding: 0.35rem 0.6rem;
}

.queue-item {
  align-items: center;
  border-top: 1px solid #eef1f6;
  display: flex;
  gap: 1rem;
  justify-content: space-between;
  padding: 1rem 0;
}

.queue-item__content {
  flex: 1;
}

@media (max-width: 767.98px) {
  .hero-actions {
    flex-direction: column;
    align-items: flex-start;
  }

  .queue-item,
  .quick-action {
    align-items: flex-start;
    flex-direction: column;
  }
}
</style>
